import * as Tone from 'tone';

// Audio export. Plays the active pattern for `bars` loops while recording the
// master output via Tone.Recorder (MediaRecorder under the hood). The webm
// blob is decoded through an AudioContext and re-encoded as a 16-bit PCM WAV
// so the user gets a true .wav file regardless of browser codec choice.
//
// Real-time render only — duration matches playback time. A future upgrade
// path is OfflineAudioContext, which would render faster than realtime, but
// requires rebuilding the entire audio graph in the offline context.

export async function exportProject(ctx, { bars = 4, tailSeconds = 1.5 } = {}) {
    const pattern = ctx.patterns.getActive();
    const bpm = ctx.scheduler.getBPM();
    const sixteenthSec = (60 / bpm) / 4;
    const oneLoopSec = pattern.length * sixteenthSec;
    const totalSec = oneLoopSec * bars + tailSeconds;

    const tap = ctx.mixer.getOutputTap();
    if (!tap) throw new Error('mixer not initialized');

    const recorder = new Tone.Recorder();
    tap.connect(recorder);

    ctx.scheduler.stop();
    await recorder.start();
    ctx.scheduler.play();

    await new Promise(r => setTimeout(r, totalSec * 1000));

    ctx.scheduler.stop();
    const recordedBlob = await recorder.stop();
    try { tap.disconnect(recorder); } catch (_) { /* already gone */ }
    recorder.dispose();

    return await blobToWav(recordedBlob);
}

async function blobToWav(blob) {
    const arrayBuffer = await blob.arrayBuffer();
    // Reuse Tone's existing AudioContext — the user already granted audio
    // permission, and a fresh `new AudioContext()` can fail on some browsers.
    const audioCtx = Tone.getContext().rawContext || new AudioContext();
    const audioBuffer = await audioCtx.decodeAudioData(arrayBuffer);
    return audioBufferToWavBlob(audioBuffer);
}

// 16-bit PCM WAV encoder. Standard RIFF/WAVE/fmt /data layout.
function audioBufferToWavBlob(buffer) {
    const channels = buffer.numberOfChannels;
    const sampleRate = buffer.sampleRate;
    const samples = buffer.length;
    const dataBytes = samples * channels * 2;
    const out = new ArrayBuffer(44 + dataBytes);
    const view = new DataView(out);

    writeString(view, 0, 'RIFF');
    view.setUint32(4, 36 + dataBytes, true);
    writeString(view, 8, 'WAVE');
    writeString(view, 12, 'fmt ');
    view.setUint32(16, 16, true);             // fmt chunk size
    view.setUint16(20, 1, true);              // PCM
    view.setUint16(22, channels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * channels * 2, true); // byte rate
    view.setUint16(32, channels * 2, true);   // block align
    view.setUint16(34, 16, true);             // bits per sample
    writeString(view, 36, 'data');
    view.setUint32(40, dataBytes, true);

    const channelData = [];
    for (let c = 0; c < channels; c++) channelData.push(buffer.getChannelData(c));

    let offset = 44;
    for (let i = 0; i < samples; i++) {
        for (let c = 0; c < channels; c++) {
            const s = Math.max(-1, Math.min(1, channelData[c][i]));
            view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
            offset += 2;
        }
    }
    return new Blob([out], { type: 'audio/wav' });
}

function writeString(view, offset, str) {
    for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i));
}

export function downloadBlob(blob, filename) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}
